import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';

const BryModal = ({ isOpen, onClose, title, children, footer, size = 'md' }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="bry-modal-backdrop"
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            padding: '16px',
          }}
        >
          <motion.div 
            ref={modalRef}
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ type: 'spring', damping: 26, stiffness: 260 }}
            className="bry-modal-sheet"
            style={{
              background: 'var(--bry-bg-card, #FFFFFF)',
              borderRadius: '24px',
              border: '1px solid rgba(15, 23, 42, 0.08)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              width: '100%',
              maxWidth: size === 'lg' ? '600px' : size === 'xl' ? '800px' : '480px',
              maxHeight: '90dvh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(15, 23, 42, 0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bry-bg-card, #FFFFFF)', flexShrink: 0 }}>
              <h3 id="modal-title" style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--bry-text-primary, #0F172A)', margin: 0, letterSpacing: '-0.01em' }}>
                {title}
              </h3>
              <button 
                onClick={onClose}
                style={{ background: 'rgba(15, 23, 42, 0.06)', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0F172A', cursor: 'pointer', fontSize: '16px', fontWeight: '700', transition: 'all 0.15s ease' }}
                aria-label="Cerrar modal"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '16px 20px', color: 'var(--bry-text-primary, #0F172A)', overflowY: 'auto', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y', flex: 1 }}>
              {children}
            </div>

            {/* Footer */}
            {footer && (
              <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(15, 23, 42, 0.08)', background: 'var(--bry-bg-card, #FFFFFF)', display: 'flex', justifyContent: 'flex-end', gap: '12px', flexShrink: 0 }}>
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

BryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  footer: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
};

export default BryModal;
